import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Button, Form, FormField, Input, Select, SpaceBetween } from "@cloudscape-design/components";
import { getAuthToken } from "./auth";
import { getAwsCreds, getDefaultRegion } from "./awsCredentials";
import { apiFetch } from "./apiFetch";

interface AttachStacksFormProps {
  projectId: string;
  defaultTagKey?: string;
  defaultTagValue?: string;
  defaultAccountAlias?: string;
  hideSubmitButton?: boolean;
  onSuccess: (result: { imported: number; updated: number; errors: number }) => void;
  onError: (message: string) => void;
  onLoadingChange?: (loading: boolean) => void;
}

export interface AttachStacksFormHandle {
  submit: () => void;
}

const AttachStacksForm = forwardRef<AttachStacksFormHandle, AttachStacksFormProps>(({
  projectId,
  defaultTagKey = "ICLProject",
  defaultTagValue = "",
  defaultAccountAlias,
  hideSubmitButton,
  onSuccess,
  onError,
  onLoadingChange,
}, ref) => {
  const [tagKey, setTagKey] = useState(defaultTagKey);
  const [tagValue, setTagValue] = useState(defaultTagValue);
  const [accountOptions, setAccountOptions] = useState<{ value: string; label: string; alias: string }[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<{ value: string; label: string; alias: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    apiFetch(`/accounts`)
      .then((r) => r.json())
      .then((json) => {
        const opts = json.data.map((a: any) => ({ value: a.accountid, label: `${a.Alias} (${a.accountid})`, alias: a.Alias }));
        setAccountOptions(opts);
        if (defaultAccountAlias) {
          const match = opts.find((o: any) => o.alias === defaultAccountAlias);
          if (match) setSelectedAccount(match);
        }
      })
      .catch(() => {});
  }, [defaultAccountAlias]);

  const handleSubmit = async () => {
    if (!selectedAccount) { onError("Please select an account"); return; }
    const creds = getAwsCreds(selectedAccount.alias);
    if (!creds) { onError(`No AWS credentials for account "${selectedAccount.alias}". Please add credentials in Settings.`); return; }

    setSubmitting(true);
    onLoadingChange?.(true);
    try {
      const res = await apiFetch(`/stacks/import-by-project`, {
        method: "POST",
        headers: {
          "x-aws-access-key-id": creds.accessKeyId,
          "x-aws-secret-access-key": creds.secretAccessKey,
          "x-aws-session-token": creds.sessionToken,
        },
        body: JSON.stringify({ tagKey, tagValue, projectId, accountId: selectedAccount.value, region: getDefaultRegion() }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      const result = await res.json();

      // Create workloads for each imported stack
      for (const stack of result.stacks ?? []) {
        try {
          await apiFetch(`/stacks/${stack.documentId}/create-workloads`, {
            method: "POST",
          });
        } catch {}
      }

      onSuccess({ imported: result.imported, updated: result.updated, errors: result.errors });
    } catch (e: any) {
      onError(e.message);
    } finally {
      setSubmitting(false);
      onLoadingChange?.(false);
    }
  };

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  return (
    <Form actions={!hideSubmitButton ? <Button variant="primary" onClick={handleSubmit} loading={submitting}>Attach Stacks</Button> : undefined}>
      <SpaceBetween size="m">
        <FormField label="Account" description="Select the AWS account to search for stacks">
          <Select
            selectedOption={selectedAccount}
            onChange={({ detail }) => setSelectedAccount(detail.selectedOption as any)}
            options={accountOptions}
            placeholder="Select account"
            filteringType="auto"
          />
        </FormField>
        <FormField label="Tag Key">
          <Input value={tagKey} onChange={({ detail }) => setTagKey(detail.value)} />
        </FormField>
        <FormField label="Tag Value">
          <Input value={tagValue} onChange={({ detail }) => setTagValue(detail.value)} />
        </FormField>
      </SpaceBetween>
    </Form>
  );
});

export default AttachStacksForm;
